import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeader } from "../../../utils/authHeader";
import { cn } from "../../../utils/classMerger";
import { useIsMobile } from "../../../hooks/useIsMobile";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../Select";
import ColorField from "../ColorField";
import {
    BrandColors,
    BrandPalette,
    ColorSource,
    DEFAULT_BRAND_COLORS,
} from "./CaptionedVideoMediaStep";

// Reusable brand-color selector shared by the captioned-video flow and the
// slideshow. Same 3-color (main / secondary / accent) model as ZapCap, sourced
// from the user's brokerage (GET /settings/brokerages/list) and saved branding
// preference (GET /settings/branding), with an optional custom-color override.

const CUSTOM_OPTION_ID = "__custom__";
const CUSTOM_OPTION_LABEL = "Custom — Pick your own colors";

const COLOR_FIELDS: ReadonlyArray<{ key: keyof BrandColors; label: string }> = [
    { key: "main", label: "Primary" },
    { key: "secondary", label: "Accent" },
    { key: "accent", label: "Text & Background" },
];

interface BrandColorSelectorProps {
    /** Controlled color source. When omitted the component manages its own. */
    value?: ColorSource;
    /** Fired whenever the color source changes (brokerage swap or custom edit). */
    onChange?: (source: ColorSource) => void;
    /** Convenience callback with the resolved colors. */
    onColorsChange?: (colors: BrandColors) => void;
    /** Fired with the selected brokerage's label, or undefined for custom colors. */
    onBrokerageLabelChange?: (label: string | undefined) => void;
    className?: string;
}

const BrandColorSelector: React.FC<BrandColorSelectorProps> = ({
    value,
    onChange,
    onColorsChange,
    onBrokerageLabelChange,
    className = "",
}) => {
    const isMobile = useIsMobile(700);
    const [palettes, setPalettes] = useState<BrandPalette[]>([]);
    const [internalSource, setInternalSource] = useState<ColorSource | null>(
        value ?? null
    );

    const colorSource: ColorSource =
        value ??
        internalSource ?? {
            type: "brokerage",
            id: palettes[0]?.id ?? "default",
        };

    const emit = (next: ColorSource): void => {
        if (value === undefined) setInternalSource(next);
        onChange?.(next);
    };

    useEffect(() => {
        const fetchInitialData = async (): Promise<void> => {
            try {
                const authHeader = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

                const [brokeragesRes, brandingRes] = await Promise.all([
                    axios.get<
                        { _id: string; name: string; zapcapColors: string[] }[]
                    >(`${backendUrl}/settings/brokerages/list`, {
                        headers: authHeader,
                    }),
                    axios.get<{
                        useCustomColorsForZapcap: boolean;
                        brokerageId: string;
                        mainColor: string;
                        secondColor: string;
                        thirdColor: string;
                    }>(`${backendUrl}/settings/branding`, {
                        headers: authHeader,
                    }),
                ]);

                const mapped: BrandPalette[] = brokeragesRes.data.map((b) => ({
                    id: b._id,
                    label: b.name,
                    colors: {
                        main: b.zapcapColors[0] ?? "#000000",
                        secondary: b.zapcapColors[1] ?? "#000000",
                        accent: b.zapcapColors[2] ?? "#FFFFFF",
                    },
                }));
                setPalettes(mapped);

                // Only seed the source from saved branding when uncontrolled.
                if (value === undefined) {
                    const branding = brandingRes.data;
                    if (branding.useCustomColorsForZapcap) {
                        emit({
                            type: "custom",
                            colors: {
                                main: branding.mainColor,
                                secondary: branding.secondColor,
                                accent: branding.thirdColor,
                            },
                        });
                    } else {
                        emit({
                            type: "brokerage",
                            id: branding.brokerageId,
                        });
                    }
                }
            } catch {
                // Silently fall back to defaults already in state.
            }
        };
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resolveBrokerage = (id: string): BrandPalette =>
        palettes.find((p) => p.id === id) ??
        palettes[0] ?? { id: "", label: "", colors: DEFAULT_BRAND_COLORS };

    const isCustom = colorSource.type === "custom";
    const effectiveColors: BrandColors = isCustom
        ? colorSource.colors
        : resolveBrokerage(colorSource.id).colors;
    const sourceLabel = isCustom
        ? CUSTOM_OPTION_LABEL
        : resolveBrokerage(colorSource.id).label;

    useEffect(() => {
        onColorsChange?.(effectiveColors);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveColors.main, effectiveColors.secondary, effectiveColors.accent]);

    useEffect(() => {
        onBrokerageLabelChange?.(isCustom ? undefined : sourceLabel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCustom, sourceLabel]);

    const handleSourceDropdownChange = (id: string): void => {
        if (id === CUSTOM_OPTION_ID) {
            emit({ type: "custom", colors: { ...effectiveColors } });
        } else {
            emit({ type: "brokerage", id });
        }
    };

    const updateColor = (key: keyof BrandColors, hex: string): void => {
        emit({ type: "custom", colors: { ...effectiveColors, [key]: hex } });
    };

    return (
        <div className={cn("space-y-3", className)}>
            <div>
                <h4 className="text-base font-semibold text-primary">
                    Brand Colors
                </h4>
                <p className="text-sm text-neutral-500">
                    Colors are set to your brokerage brand. You can customize
                    below.
                </p>
            </div>

            <Select
                value={isCustom ? CUSTOM_OPTION_ID : colorSource.id}
                onValueChange={handleSourceDropdownChange}
            >
                <SelectTrigger>
                    <SelectValue placeholder="Select brokerage" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={CUSTOM_OPTION_ID}>
                        {CUSTOM_OPTION_LABEL}
                    </SelectItem>
                    {palettes.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                            {p.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <div className="pt-2">
                <div className="flex items-baseline justify-between mb-3">
                    <h5 className="text-xs font-bold text-neutral-500 tracking-wider">
                        {isCustom ? "EDIT COLORS" : "COLOR PREVIEW"}
                    </h5>
                    {isCustom && (
                        <span className="text-xs text-neutral-500">
                            Click any color to edit
                        </span>
                    )}
                </div>
                <div
                    className={cn(
                        "grid gap-2",
                        isMobile ? "grid-cols-2" : "grid-cols-3"
                    )}
                >
                    {COLOR_FIELDS.map(({ key, label }) => (
                        <ColorField
                            key={key}
                            label={label}
                            color={effectiveColors[key]}
                            disabled={!isCustom}
                            onChange={(val) => updateColor(key, val)}
                        />
                    ))}
                </div>
                <p className="text-xs text-neutral-500 mt-3">
                    {isCustom
                        ? "Using custom colors — selecting a brokerage above will discard them"
                        : `Using ${sourceLabel} brand palette`}
                </p>
            </div>
        </div>
    );
};

export default BrandColorSelector;
